import React, { useState } from 'react';
import { Modal, ModalHeader, ModalContent, ModalFooter, Button, Input } from '@/shared/components/ui';
import type { PopulatedLead } from '../types';
import type { LeadKanbanCard } from '@troia-v3/types';
import { Icon } from '@/shared/icons';

/**
 * Props do AlertSnoozeDialog
 */
export interface AlertSnoozeDialogProps {
  /** Lead cujo alerta será silenciado */
  lead: PopulatedLead | LeadKanbanCard | null;
  /** Controla se o modal está aberto */
  isOpen: boolean;
  /** Callback para fechar o modal */
  onClose: () => void;
  /** Callback ao confirmar um preset/data — recebe o ISO de `until` */
  onConfirm: (until: string) => void | Promise<void>;
  /** Estado de loading externo (ex: mutation em andamento) */
  loading?: boolean;
}

const PRESETS = [
  { label: '7 dias', days: 7 },
  { label: '30 dias', days: 30 },
  { label: '60 dias', days: 60 },
];

function addDaysIso(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString();
}

function todayInputValue(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Modal para silenciar o alerta de inatividade de um lead.
 *
 * Chrome = Modal shared (F72). Oferece presets rápidos (7/30/60 dias) ou uma
 * data customizada. Ao confirmar, chama `onConfirm(untilISO)` — o caller decide
 * como persistir (POST /leads/:id/alert-snooze) e invalidar o cache.
 *
 * @example
 * ```tsx
 * <AlertSnoozeDialog
 *   lead={snoozeLead}
 *   isOpen={isSnoozeOpen}
 *   onClose={() => setIsSnoozeOpen(false)}
 *   onConfirm={(until) => leadsService.snoozeAlert(snoozeLead.id, until)}
 * />
 * ```
 */
export const AlertSnoozeDialog: React.FC<AlertSnoozeDialogProps> = ({
  lead,
  isOpen,
  onClose,
  onConfirm,
  loading = false,
}) => {
  const [customDate, setCustomDate] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen || !lead) return null;

  const isBusy = loading || submitting;

  const handleClose = () => {
    if (isBusy) return;
    setCustomDate('');
    onClose();
  };

  const applyUntil = async (until: string) => {
    setSubmitting(true);
    try {
      await onConfirm(until);
      setCustomDate('');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePresetClick = (days: number) => {
    if (isBusy) return;
    void applyUntil(addDaysIso(days));
  };

  const handleCustomApply = () => {
    if (isBusy || !customDate) return;
    const until = new Date(`${customDate}T23:59:59`).toISOString();
    void applyUntil(until);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} maxWidth="md">
      <div data-testid="alert-snooze-dialog">
        <ModalHeader onClose={handleClose}>
          <div
            className="w-9 h-9 rounded-[9px] flex items-center justify-center flex-none"
            style={{ backgroundColor: '#f3e8ff', color: '#7c3aed' }}
          >
            <Icon name="bell-off" className="w-5 h-5" />
          </div>
          <h2 className="text-lg font-semibold" style={{ color: '#1e293b' }}>
            Silenciar alerta
          </h2>
        </ModalHeader>

        <ModalContent className="space-y-4">
          <div
            className="p-3 rounded-lg"
            style={{ backgroundColor: '#f1f5f9' }}
          >
            <p className="text-sm font-medium" style={{ color: '#1e293b' }}>
              {lead.contact?.name || 'Sem nome'}
            </p>
            <p className="text-xs mt-0.5" style={{ color: '#64748b' }}>
              O alerta de inatividade fica pausado até a data escolhida.
            </p>
          </div>

          {/* Presets */}
          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: '#1e293b' }}
            >
              Silenciar por
            </label>
            <div className="flex items-center gap-2">
              {PRESETS.map((preset) => (
                <Button
                  key={preset.days}
                  variant="secondary"
                  className="flex-1"
                  onClick={() => handlePresetClick(preset.days)}
                  disabled={isBusy}
                  data-testid={`alert-snooze-preset-${preset.days}`}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Data customizada */}
          <div>
            <label
              className="block text-sm font-medium mb-1.5"
              style={{ color: '#1e293b' }}
            >
              Ou escolha uma data
            </label>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={customDate}
                onChange={(e) => setCustomDate(e.target.value)}
                icon="calendar"
                disabled={isBusy}
                min={todayInputValue()}
                data-testid="alert-snooze-custom-date-input"
              />
              <Button
                onClick={handleCustomApply}
                variant="secondary"
                size="md"
                disabled={isBusy || !customDate}
                data-testid="alert-snooze-custom-apply"
              >
                Aplicar
              </Button>
            </div>
          </div>
        </ModalContent>

        <ModalFooter>
          <Button
            onClick={handleClose}
            variant="secondary"
            size="md"
            disabled={isBusy}
            data-testid="alert-snooze-dialog-cancel"
          >
            Cancelar
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
};
